import { useState, useEffect, useMemo } from "react";
import { Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import { Cell, Pie, PieChart, Tooltip as ReTooltip } from "recharts";

import WorkOutlinedIcon from "@mui/icons-material/WorkOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import { useTheme, alpha } from "@mui/material/styles";
import CustomCard from "../../components/common/CustomCard";
import CustomHeader from "../../components/common/CustomHeader";
import CustomTable from "../../components/common/CustomTable";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { adminDashboard } from "../../api/dashboardApi";


const TOKEN_LIMIT = 100000;
const INTERVIEW_LIMIT = 50;

// ─── Static data (defined outside render) ────────────────────
export const KPI_CONFIGS = [
    {
        key: "active_jobs",
        title: "Active Jobs",
        subtitle: "Live postings",
        accentColor: "primary",
        icon: WorkOutlinedIcon ,
    },
    {
        key: "total_candidates",
        title: "Candidates",
        subtitle: "Total applied",
        accentColor: "info",
        icon: PeopleOutlined,
    },
    {
        key: "interviews_scheduled",
        title: "Scheduled",
        subtitle: "Pending interviews",
        accentColor: "warning",
        icon: EventAvailableIcon,
    },
    {
        key: "interviews_completed",
        title: "Completed",
        subtitle: "Done",
        accentColor: "success",
        icon: CheckCircleOutlined ,
    },
];

const JOB_COLUMNS = [
    { field: "job_title", label: "Job Title" },
    { field: "recruiter", label: "Recruiter" },
    { field: "applications", label: "Applied" },
    {
        field: "status",
        label: "Status",
        render: (val) => <StatusChip status={val} />,
    },
];

const RECRUITER_COLUMNS = [
    { field: "recruiter", label: "Recruiter" },
    { field: "jobs_posted", label: "Jobs" },
    { 
        field: "interviews_scheduled", 
        label: "Interviews",
        render: (val) => <StatusChip status={val} />
    },
];

function KpiCard({
    title,
    value,
    subtitle,
    accentColor = "primary",
    icon: Icon,
}) {
    const theme = useTheme();

    const resolvedAccent =
        theme.palette[accentColor]?.main || accentColor;

    return (
        <CustomCard
            accentColor={accentColor}
            sx={{ "minHeight": 120, "width": "100%", "flex": 1, "overflow": "auto" }}
            contentPadding={true}
            headerPadding={false}
            border={true}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
            >
                {/* Left Content */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: resolvedAccent,
                            lineHeight: 1.2,
                        }}
                    >
                        {value ?? 0}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            mt: 0.5,
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.disabled,
                                mt: 0.5,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {/* Right Icon */}
                {Icon && (
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(resolvedAccent, 0.12),
                            border: `1px solid ${alpha(resolvedAccent, 0.3)}`,
                            color: resolvedAccent,
                            flexShrink: 0,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Box>
                )}
            </Stack>
        </CustomCard>
    );
};

function SectionCard({
    title,
    subtitle,
    accentColor = "primary",
    children,
}) {
    const theme = useTheme();

    const resolvedAccent =
        theme.palette[accentColor]?.main || accentColor;

    return (
        <CustomCard
            accentColor={accentColor}
            headerPadding={false}
            contentPadding={true}
            sx={{ flex: 1, minHeight: "100%", overflow: "auto" }}
        >
            {/* Header */}
            {(title || subtitle) && (
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    {/* Accent Bar */}
                    <Box
                        sx={{
                            width: 3,
                            height: 22,
                            borderRadius: "99px",
                            backgroundColor: resolvedAccent,
                            flexShrink: 0,
                        }}
                    />

                    {/* Title + Subtitle */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                lineHeight: 1.2,
                            }}
                        >
                            {title}
                        </Typography>

                        {subtitle && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: theme.palette.text.secondary,
                                    mt: 0.3,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            )}

            {/* Content */}
            {children}
        </CustomCard>
    );
}

function UsageBar({ label, used = 0, limit = 1, color = "primary" }) {
    const theme = useTheme();

    const pct = Math.min((used / limit) * 100, 100);

    const resolvedColor =
        theme.palette[color]?.main || color;

    return (
        <Stack spacing={0.75}>
            {/* Top Row */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography
                    sx={{
                        fontSize: 13,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                    }}
                >
                    {label}
                </Typography>

                <Typography
                    sx={{
                        fontSize: 11,
                        color: theme.palette.text.disabled,
                    }}
                >
                    {used.toLocaleString()} / {limit.toLocaleString()}
                </Typography>
            </Stack>

            {/* Track */}
            <Box
                sx={{
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: alpha(theme.palette.common.white, 0.08),
                    overflow: "hidden",
                }}
            >
                {/* Fill */}
                <Box
                    sx={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: 999,
                        backgroundColor: resolvedColor,
                        transition: "width 0.6s ease",
                    }}
                />
            </Box>

            {/* Bottom Text */}
            <Typography
                sx={{
                    fontSize: 10,
                    color: theme.palette.text.disabled,
                }}
            >
                {Math.round(pct)}% used
            </Typography>
        </Stack>
    );
}

function DonutLegendRow({
    label,
    value,
    color = "primary",
    divider = false,
}) {
    const theme = useTheme();

    const resolvedColor =
        theme.palette[color]?.main || color;

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            py={1.5}
            sx={{
                width: "100%",
                ...(divider && {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                }),
            }}
        >
            {/* Left Section */}
            <Stack direction="row" spacing={1} alignItems="center">
                {/* Dot */}
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: resolvedColor,
                        flexShrink: 0,
                    }}
                />

                {/* Label */}
                <Typography
                    sx={{
                        fontSize: 14,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                    }}
                >
                    {label}
                </Typography>
            </Stack>

            {/* Value */}
            <Typography
                sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: resolvedColor,
                    minWidth: "24px",
                    textAlign: "right",
                    fontFamily: theme.typography.fontFamily,
                }}
            >
                {value}
            </Typography>
        </Stack>
    );
}


function MiniTokenCard({ value, label, color = "primary" }) {
    const theme = useTheme();

    const resolvedColor =
        theme.palette[color]?.main || color;

    return (
        <CustomCard
            sx={{
                flex: 1,
                p: 2,
            }}
            contentPadding={false}
        >
            <Typography
                sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: resolvedColor,
                }}
            >
                {value}
            </Typography>

            <Typography
                sx={{
                    fontSize: 10,
                    color: theme.palette.text.disabled,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    mt: 0.5,
                }}
            >
                {label}
            </Typography>
        </CustomCard>
    );
}

function StatusChip({ status = "Closed" }) {
    const theme = useTheme();

    const getStatusColor = () => {
        const s = String(status || "").toUpperCase();
        if (s === "OPEN" || s === "COMPLETED" || s === "SELECTED") return theme.palette.success.main;
        if (s === "IN PROGRESS" || s === "PENDING") return theme.palette.warning.main;
        if (s === "CLOSED" || s === "REJECTED" || s === "TERMINATED") return theme.palette.error.main;
        if (s === "ATTENDED" || s === "SCHEDULED") return theme.palette.info.main;
        return theme.palette.grey[500];
    };

    const color = getStatusColor();

    return (
        <Chip
            label={status}
            size="small"
            sx={{
                textTransform: "capitalize",
                backgroundColor: alpha(color, 0.12),
                color: color,
                border: `1px solid ${alpha(color, 0.4)}`,
                fontSize: 11,
                fontWeight: 500,
                height: 22,
                letterSpacing: "0.4px",
                borderRadius: "999px",
                "& .MuiChip-label": { px: 1.25 },
            }}
        />
    );
}

function DonutTooltip({ active, payload }) {
    const theme = useTheme();

    if (!active || !payload?.length) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
            }}
        >
            <Typography
                sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                }}
            >
                {payload[0].name}
            </Typography>

            <Typography
                sx={{
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                }}
            >
                {payload[0].value} interviews
            </Typography>
        </Paper>
    );
}

function DashboardSkeleton() {
    return (
        <Box sx={{ p: 3 }}>
            {/* KPI Cards */}
            <Grid container spacing={2} mb={2}>
                {[...Array(4)].map((_, i) => (
                    <Grid item xs={12} md={3} key={i}>
                        <SkeletonLoader type="card" count={1} />
                    </Grid>
                ))}
            </Grid>

            {/* Tables */}
            <Grid container spacing={2} mb={2}>
                {[6, 6].map((md, i) => (
                    <Grid item xs={12} md={md} key={i}>
                        <SkeletonLoader
                            type="table"
                            rows={5}
                            columns={[1, 2, 3, 4, 5]}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Bottom Cards */}
            <Grid container spacing={2}>
                {[6, 6].map((md, i) => (
                    <Grid item xs={12} md={md} key={i}>
                        <SkeletonLoader type="card" count={1} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

// ─── Main Dashboard ──────────────────────────────────────────
export default function HireAIDashboard() {
    const theme = useTheme();

    const [dashboardData, setDashboardData] = useState({
        kpis: {},
        interview_status: {},
        token_usage: {},
        jobs: [],
        recruiters: [],
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [overviewRes, jobsRes, recruiterRes] = await Promise.all([
                    adminDashboard.adminOverview(),
                    adminDashboard.adminJobs(),
                    adminDashboard.adminRecruiters(),
                ]);

                setDashboardData({
                    kpis: overviewRes.kpis ?? {},
                    token_usage: overviewRes.token_usage ?? {},
                    interview_status: overviewRes.interview_status ?? {},
                    jobs: jobsRes ?? [],
                    recruiters: recruiterRes ?? [],
                });
            } catch (err) {
                console.error("Dashboard fetch error:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { kpis, interview_status, token_usage, jobs, recruiters } =
        dashboardData;

    const donutData = useMemo(
        () => [
            { name: "Completed", value: interview_status.completed ?? 0 },
            { name: "In Progress", value: interview_status.in_progress ?? 0 },
            { name: "Scheduled", value: interview_status.scheduled ?? 0 },
        ],
        [interview_status]
    );

    const totalInterviews = useMemo(
        () => donutData.reduce((acc, d) => acc + d.value, 0),
        [donutData]
    );

    const DONUT_COLORS = [
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.info.main,
    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: "100vh",
            }}
        >
            <CustomHeader title="Dashboard" />

            {loading ? (
                <DashboardSkeleton />
            ) : (
                <Box sx={{ p: 3, overflow: "auto" }}>

                    {/* ── KPI Row ── */}
                    <Grid container spacing={2} mb={2} alignItems="stretch">
                        {KPI_CONFIGS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.key} sx={{ display: "flex", flexDirection: "column" }}>
                                <KpiCard
                                    {...item}
                                    value={kpis[item.key]}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* ── Tables Row ── */}
                    <Grid container spacing={2} mb={2} alignItems="flex-start">
                        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <SectionCard
                                title="Job Performance"
                                subtitle="Live overview of all postings"
                                accentColor="success"
                                // sx={{ height: "100%", minWidth: 300 }}
                            >
                                <CustomTable columns={JOB_COLUMNS} rows={jobs} />
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <SectionCard
                                title="Recruiter Activity"
                                subtitle="Individual performance metrics"
                                accentColor="info"
                                // sx={{ height: "100%", minWidth: 300 }}
                            >
                                <CustomTable
                                    columns={RECRUITER_COLUMNS}
                                    rows={recruiters}
                                />
                            </SectionCard>
                        </Grid>
                    </Grid>

                    {/* ── Bottom Row ── */}
                    <Grid container spacing={2} alignItems="flex-start">

                        {/* Donut */}
                        <Grid item xs={12} md={6}>
                            <SectionCard
                                title="Interview Status"
                                subtitle="Distribution by stage"
                                accentColor="warning"
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={4}
                                    sx={{ width: "100%", pr: 2 }}
                                >
                                    {/* Chart */}
                                    <Box sx={{ position: "relative" }}>
                                        <PieChart width={180} height={180}>
                                            <Pie
                                                data={donutData}
                                                cx={90}
                                                cy={90}
                                                innerRadius={56}
                                                outerRadius={78}
                                                dataKey="value"
                                                strokeWidth={0}
                                            >
                                                {donutData.map((_, i) => (
                                                    <Cell key={i} fill={DONUT_COLORS[i]} />
                                                ))}
                                            </Pie>
                                            <ReTooltip content={<DonutTooltip />} />
                                        </PieChart>

                                        {/* Center */}
                                        <Stack
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 24,
                                                    fontWeight: 800,
                                                    color: theme.palette.text.primary,
                                                }}
                                            >
                                                {totalInterviews}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    color: theme.palette.text.disabled,
                                                    letterSpacing: "1px",
                                                }}
                                            >
                                                TOTAL
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    {/* Legend */}
                                    <Box sx={{ flex: 1 }}>
                                        {donutData.map((entry, i) => (
                                            <DonutLegendRow
                                                key={entry.name}
                                                label={entry.name}
                                                value={entry.value}
                                                color={DONUT_COLORS[i]}
                                                divider={i < donutData.length - 1}
                                            />
                                        ))}
                                    </Box>
                                </Stack>
                            </SectionCard>
                        </Grid>

                        {/* Usage */}
                        <Grid item xs={12} md={6}>
                            <SectionCard
                                title="AI Usage"
                                subtitle="Token consumption & credits"
                                accentColor="error"
                                sx={{ height: "100%", minWidth: "300" }}
                            >
                                <Stack spacing={2.5}>

                                    <UsageBar
                                        label="AI Interviews"
                                        used={kpis.interviews_completed ?? 0}
                                        limit={INTERVIEW_LIMIT}
                                        color="success"
                                    />

                                    <UsageBar
                                        label="Total Tokens"
                                        used={token_usage.total_tokens ?? 0}
                                        limit={TOKEN_LIMIT}
                                        color="warning"
                                    />

                                    <Box
                                        sx={{
                                            height: 1,
                                            backgroundColor: alpha(
                                                theme.palette.divider,
                                                0.6
                                            ),
                                        }}
                                    />

                                    <Stack direction="row" spacing={1.5}>
                                        <MiniTokenCard
                                            value={`${(
                                                (token_usage.input_tokens ?? 0) / 1000
                                            ).toFixed(1)}K`}
                                            label="Input"
                                            color="info"
                                        />
                                        <MiniTokenCard
                                            value={`${(
                                                (token_usage.output_tokens ?? 0) / 1000
                                            ).toFixed(1)}K`}
                                            label="Output"
                                            color="error"
                                        />
                                    </Stack>
                                </Stack>
                            </SectionCard>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}